/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button, breakpoints } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ShareProfileLinkDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/ShareProfileLinkDialog';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  privacyBtnContainer: () => css`
    position: relative;
    border-top: 1px solid var(--cds-color-grey-50);
    padding-top: var(--cds-spacing-50);
    margin-top: var(--cds-spacing-150);

    ${breakpoints.down('xs')} {
      width: calc(100% + var(--cds-spacing-600));
      margin-left: calc(var(--cds-spacing-300) * -1);
    }
  `,
};

const PrivacySettingsLink = () => {
  const { editable, sharedEventingV3Data } = useContext(ProfileContext);
  const [showPrivacySettingsDialog, setShowPrivacySettingsDialog] = useState<boolean>(false);
  const retrackedTrack = useRetracked();
  const track = useTracker();
  const visibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: EVENTS.updateIntroProfileVisibility,
    },
    ...sharedEventingV3Data,
  });

  if (!editable) return null;

  return (
    <>
      {/* I wrap the button with a TrackedDiv to support retracked visibility tracking since I cannot use tracked components on
        CDS buttons. It breaks eventing V3's visibility tracking, which relies on an HTML ref. Using component=
        {TrackedButton} on a CDS button causes the ref to refer to the TrackedButton component itself, *not* the
        underlying HTML element, which in turn results in the IntersectionObserver used in `useVisibilityTracker()` to 
        error out since we're not referencing an html element. */}
      <TrackedDiv
        withVisibilityTracking
        requireFullyVisible={false}
        trackClicks={false}
        trackingName={EVENTS.updateIntroProfileVisibility}
        css={styles.privacyBtnContainer}
      >
        <Button
          ref={visibilityTrackingRef}
          onClick={() => {
            retrackedTrack({
              trackingName: EVENTS.updateIntroProfileVisibility,
              trackingData: {},
              action: 'click',
            });

            track('click_button', {
              button: {
                name: EVENTS.updateIntroProfileVisibility,
              },
              ...sharedEventingV3Data,
            });
            setShowPrivacySettingsDialog(true);
          }}
          variant="ghost"
        >
          {_t('Update profile visibility')}
        </Button>
      </TrackedDiv>

      {showPrivacySettingsDialog && (
        <ShareProfileLinkDialog
          isOpen={showPrivacySettingsDialog}
          onClose={() => setShowPrivacySettingsDialog(false)}
          openPrivacySettingsDialog={true}
        />
      )}
    </>
  );
};

export default PrivacySettingsLink;
